import React, { useState } from "react";
import { useNavigate } from "react-router";
import { newAxiosClient } from "@shadowcue/ui";
import type { IUser } from "../../../types/iuser";
import ScriptTemplateForm from "./form";
import GenerateScriptModal, { generateMode } from "./generator/modal";
import {
  blankScriptTemplate,
  validate,
  scrollToField,
  withClientIds,
  scriptTemplateToPayload
} from "./form_helpers";
import {
  PageContainer,
  PageTopbar,
  StickyActionBar,
  astryx,
  neutralTheme,
  useDocumentTheme,
  Sparkles,
  type IScriptTemplate
} from "@shadowcue/ui";

interface NewScriptTemplateProps {
  currentUser: IUser;
}

const axios = newAxiosClient();

const NewScriptTemplate = ({ currentUser }: NewScriptTemplateProps) => {
  const [formValues, setFormValues] = useState<IScriptTemplate>(blankScriptTemplate)
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // The generate modal is open by default
  const [generateModalOpen, setGenerateModalOpen] = useState<boolean>(true);
  const [generateModalMode, setGenerateModalMode] = useState<generateMode>(generateMode.INITIAL)
  const generateButtonText = generateModalMode === generateMode.REGENERATE ? "Regenerate" : "Generate"
  const navigate = useNavigate();
  const mode = useDocumentTheme();

  const clearError = (key: string) => {
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleGenerated = (template: IScriptTemplate) => {
    // Backfills the clientIds the drag-and-drop builder needs, the same way
    // edit.tsx does for templates fetched from the API.
    setFormValues(withClientIds(template));
    setErrors({});
    setGenerateModalMode(generateMode.REGENERATE);
  };

  const handleSubmit = async () => {
    const validationErrors = validate(formValues);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      scrollToField(Object.keys(validationErrors)[0]);
      return;
    }

    setErrors({});

    const payload = scriptTemplateToPayload(formValues)

    setSubmitting(true);
    setSubmitError(null);
    try {
      await axios.post(`/api/script_templates/`, payload, {
        headers: { AUTHORIZATION: `Bearer ${currentUser.authToken}` },
        withCredentials: true,
      });
      // Replace, not push: Back would otherwise re-seed this form from the
      // router state and offer the already-saved draft as if it were unsaved.
      navigate("/admin/script_templates", { replace: true });
    } catch (error) {
      setSubmitError("Failed to save the template. Please try again.");
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <astryx.Theme theme={neutralTheme} mode={mode}>
      <astryx.LayerProvider>
        <PageContainer>
          <PageTopbar
            breadcrumbs={[
              { label: "Script templates", href: "/admin/script_templates" },
              { label: "Create" },
            ]}
          />

          <ScriptTemplateForm
            values={formValues}
            onChange={setFormValues}
            errors={errors}
            onClearError={clearError}
            loading={false}
            headerAction={
              <astryx.Button
                label={generateButtonText}
                variant="secondary"
                icon={<astryx.Icon icon={Sparkles} />}
                onClick={() => setGenerateModalOpen(true)}
              />
            }
          />

          <StickyActionBar error={submitError ?? undefined}>
            <astryx.Button
              label="Cancel"
              variant="ghost"
              onClick={() => navigate("/admin/script_templates")}
            />
            <astryx.Button
              label="Create script"
              variant="primary"
              isLoading={submitting}
              onClick={handleSubmit}
            />
          </StickyActionBar>

          <GenerateScriptModal
            isOpen={generateModalOpen}
            mode={generateModalMode}
            onClose={() => setGenerateModalOpen(false)}
            scriptTemplate={formValues}
            onGenerated={handleGenerated}
            currentUser={currentUser}
          />
        </PageContainer>
      </astryx.LayerProvider>
    </astryx.Theme>
  );
};

export default NewScriptTemplate;
